import { Checkbox, Table as ChakraTable, Icon, Box } from '@chakra-ui/react';
import {
  useATPageSize,
  useATPageIndex,
  useATSelection,
  useATisActive,
  useATColumns,
  ATColumnType,
} from '../hooks';
import type { ATRenderRowStyleProps, ATRowsProps } from '../actionTable/ATTypes';
import { TableCells } from './TableCells';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Ref, useEffect, useRef, useState } from 'react';

// Table rows
export const TableRows = <
  T extends Record<string, any>,
  TExtra extends Record<string, any> = {},
  TContext = any,
>({
  sortedItems,
  isPaginated,
  isPaginatedAsync,
  isCheckbox,
  isSelectionDisabled,
  isRowSelectionDisabled,
  isMultipleSelection,
  rowHeight,
  renderCell,
  renderRow,
  renderContext,
  onRowClick,
  onRowDoubleClick,
  scrollRef,
  rowKey,
  ...rest
}: ATRowsProps<T, TExtra, TContext>) => {
  const { selection } = useATSelection();
  const { isActive, setIsActive } = useATisActive();
  const { pageIndex } = useATPageIndex();
  const { pageSize } = useATPageSize();

  const rowItems = (() => {
    if (!Array.isArray(sortedItems)) {
      return [];
    }
    return isPaginatedAsync
      ? sortedItems
      : isPaginated
        ? sortedItems.slice(pageSize * (pageIndex - 1), pageSize * pageIndex)
        : sortedItems;
  })();

  // useEffect(() => {
  //   setRowItems(
  //     isPaginatedAsync
  //       ? sortedItems
  //       : sortedItems.slice(pageSize * (pageIndex - 1), pageSize * pageIndex),
  //   );
  // }, [sortedItems, pageIndex, pageSize]);

  const virtualizer = useVirtualizer({
    count: isPaginated ? rowItems.length : rowItems.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight, // rows height
    overscan: 1, // rows to render
    scrollPaddingEnd: 1, // padding at the end of the table
    scrollPaddingStart: 1, // padding at the start of the table
    gap: 1, // gap between rows
  });

  return (
    <ChakraTable.Root
      interactive
      height="fit-content"
      width="fit-content"
      position="relative"
      top={0}
      left={0}
      right={0}
      {...rest}
    >
      <ChakraTable.Body
        style={{
          height: `${virtualizer.getTotalSize()}px`,
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const item = rowItems[virtualItem.index] as T;
          // Use WeakMap-based has() for O(1) lookup instead of O(n) array search
          const isSelected = selection.has((item as any)[rowKey]);
          const rowStyle: ATRenderRowStyleProps<T & TExtra> = renderRow
            ? renderRow({
                rowValue: item as T & TExtra,
                rowIdx: virtualItem.index,
                context: renderContext,
              })
            : {
                rowProps: undefined,
                cellProps: undefined,
              };

          const isRowSelectionDisabledValue =
            isSelectionDisabled ||
            (isRowSelectionDisabled
              ? isRowSelectionDisabled(item as T & TExtra)
              : false);

          const onSelect = () => {
            if (!isRowSelectionDisabledValue) {
              setIsActive(true);
              if (!isMultipleSelection) {
                // For single selection, clear and add immediately
                selection.toggleSingleImmediate(item, rowKey);
              } else {
                selection.toggleImmediate(item, rowKey);
              }
            }
          };

          const onSelectDebounced = () => {
            if (!isRowSelectionDisabledValue) {
              setIsActive(true);
              if (!isMultipleSelection) {
                // For single selection
                selection.toggleSingle(item, rowKey);
              } else {
                selection.toggle(item, rowKey);
              }
            }
          };

          return (
            <ChakraTable.Row
              key={`${String(rowKey)}-${virtualItem.index}`}
              data-selected={isSelected ? '' : undefined}
              cursor={isRowSelectionDisabledValue ? 'default' : 'pointer'}
              style={{
                height: `${virtualItem.size}px`,
                minWidth: 'fit-content',
                position: 'absolute',
                top: 1,
                left: 0,
                right: 0,
                bottom: 1,
                transform: `translateY(${virtualItem.start}px)`,
                display: 'flex',
              }}
              {...(onRowClick && onRowDoubleClick
                ? {
                    onClick: () => onRowClick(item as T & TExtra),
                    onDoubleClick: () => onRowDoubleClick(item as T & TExtra),
                  }
                : onRowClick && !onRowDoubleClick
                  ? {
                      onClick: () => onRowClick(item as T & TExtra),
                      onDoubleClick: onSelectDebounced,
                    }
                  : onRowDoubleClick && !onRowClick
                    ? {
                        onClick: onSelectDebounced,
                        onDoubleClick: () =>
                          onRowDoubleClick(item as T & TExtra),
                      }
                    : { onClick: onSelectDebounced })}
              _hover={
                isRowSelectionDisabledValue
                  ? undefined
                  : { bg: 'primary.muted' }
              }
              {...rowStyle?.rowProps}
              bg={
                isSelected
                  ? 'primary.bg'
                  : ((rowStyle?.rowProps as any)?.bg ?? 'transparent')
              }
            >
              {isCheckbox && (
                <ChakraTable.Cell
                  alignContent="center"
                  borderRight="1px solid"
                  borderColor="border.subtle"
                  onClick={(event) => {
                    if (onRowClick && onRowDoubleClick) {
                      event.stopPropagation();
                      onSelect();
                    }
                  }}
                >
                  <Checkbox.Root
                    size="sm"
                    colorPalette="blue"
                    disabled={isRowSelectionDisabledValue}
                    checked={isSelected}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control>
                      <Checkbox.Indicator />
                    </Checkbox.Control>
                  </Checkbox.Root>
                </ChakraTable.Cell>
              )}
              <TableCells<T, TExtra, TContext>
                item={item}
                rowHeight={rowHeight}
                virtualItem={virtualItem}
                renderCell={renderCell}
                rowPropsForCell={rowStyle.cellProps}
                renderContext={renderContext}
              />
            </ChakraTable.Row>
          );
        })}
      </ChakraTable.Body>
    </ChakraTable.Root>
  );
};
